import {
    PropsWithChildren,
    createContext,
    useContext,
    useState,
} from "react";
import useIsBelowLg from "../hooks/useIsBelowLg";

interface ContextState {
    theme: "light" | "dark";
    isSidebarOpen: boolean;
    setIsSidebarOpen: (isSidebarOpen: boolean) => void;
    updateTheme: (isDark: boolean) => void;
}

const defaultContext: ContextState = {
    theme: "light",
    isSidebarOpen: false,
    setIsSidebarOpen: () => {},
    updateTheme: () => {},
};

const ThemeContext = createContext<ContextState>(defaultContext);

const ThemeContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
    // read on first render, not in an effect — theme-derived assets (the logo)
    // would otherwise paint the light variant for one frame
    const [theme, setTheme] = useState<"light" | "dark">(
        () => (localStorage.getItem("theme") as "light" | "dark") ?? "light"
    );
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const isBelowLg = useIsBelowLg();

	const updateTheme = (isDark: boolean) => {
        const theme = isDark ? "dark" : "light";
        localStorage.setItem("theme", theme);
        setTheme(theme as "light" | "dark");
        document.documentElement.setAttribute("data-theme", theme);
		if(isDark) {
			document.documentElement.classList.add('dark')
		} else {
			document.documentElement.classList.remove('dark')
		}
		
    };

	return (
		<ThemeContext.Provider
			value={{
				theme,
				updateTheme,
                isSidebarOpen: isSidebarOpen && isBelowLg,
                setIsSidebarOpen,
			}}>
			{children}
		</ThemeContext.Provider>
	);
};

const useThemeContext = () => useContext(ThemeContext);

export { ThemeContextProvider, useThemeContext };